/**
 * ThumbnailQueue — THE pending-work model for element-picture generation.
 *
 * Pure and synchronous (fully unit-tested): dedup, FIFO order, per-item retry
 * budget, and generation-scoped clearing. The async machinery (hidden viewer,
 * screenshots, uploads) lives in useElementThumbnails and consumes this queue
 * one item at a time — keeping the fragile part thin and the logic testable.
 */

export interface ThumbnailTask {
  poiId: string;
}

const MAX_ATTEMPTS = 2;

export class ThumbnailQueue {
  private order: string[] = [];
  private queued = new Set<string>(); // membership mirror of `order` (O(1) dedup)
  private attempts = new Map<string, number>();
  private done = new Set<string>();

  /** Add tasks; already-queued, exhausted, or completed poiIds are ignored. */
  enqueue(poiIds: string[]): number {
    let added = 0;
    for (const raw of poiIds) {
      const poiId = String(raw);
      if (!poiId) continue;
      if (this.done.has(poiId)) continue;
      if (this.queued.has(poiId)) continue;
      if ((this.attempts.get(poiId) || 0) >= MAX_ATTEMPTS) continue;
      this.order.push(poiId);
      this.queued.add(poiId);
      added++;
    }
    return added;
  }

  /** Next task to work on (stays queued until reported done/failed). */
  peek(): ThumbnailTask | null {
    return this.order.length > 0 ? { poiId: this.order[0] } : null;
  }

  /** Mark the current task successfully generated. */
  complete(poiId: string): void {
    this.done.add(poiId);
    this.remove(poiId);
  }

  /**
   * Mark the current task failed. Consumes one attempt; the task is re-queued
   * at the BACK (so one broken element cannot block the batch) until its
   * attempt budget runs out.
   */
  fail(poiId: string): void {
    const used = (this.attempts.get(poiId) || 0) + 1;
    this.attempts.set(poiId, used);
    this.remove(poiId);
    if (used < MAX_ATTEMPTS) {
      this.order.push(poiId);
      this.queued.add(poiId);
    }
  }

  /** Drop a task without consuming attempts (e.g. no dbId — permanently skip). */
  skip(poiId: string): void {
    this.attempts.set(poiId, MAX_ATTEMPTS);
    this.remove(poiId);
  }

  /** Abandon everything pending (site switch / teardown). Completed memory stays. */
  clear(): void {
    this.order = [];
    this.queued.clear();
  }

  get size(): number {
    return this.order.length;
  }

  private remove(poiId: string): void {
    this.order = this.order.filter(id => id !== poiId);
    this.queued.delete(poiId);
  }
}
